import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
const bookTableImage = "/assets/book-table.webp";
const reserveTableImage = "/assets/reserve-table.webp";

const inputCls = "w-[250px] max-[480px]:w-full max-w-full p-2.5 text-lg border border-[#ccc] rounded-[10px] cursor-pointer font-[inherit] bg-white text-black placeholder-black";

function BookTablePage() {
  const [isLoading, setIsLoading] = useState(false);
  const [dateType, setDateType] = useState("text");
  const [formData, setFormData] = useState({
    seats: "", date: "", time: "", firstName: "", lastName: "",
    phone: "", email: "", specialRequest: "",
  });

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 13; hour <= 21; hour++) {
      for (let minutes = 0; minutes < 60; minutes += 15) {
        const timeString = new Date(0, 0, 0, hour, minutes).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
        options.push(timeString);
      }
    }
    return options;
  };

  const handleDateFocus = () => setDateType("date");
  const handleDateBlur = (e) => { if (!e.target.value) setDateType("text"); };
  const handleChange = (e) => { const { name, value } = e.target; setFormData({ ...formData, [name]: value }); };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    const message = `*New Table Booking*\n\n*Name:* ${formData.firstName} ${formData.lastName}\n*Phone:* ${formData.phone}\n*Email:* ${formData.email}\n*Seats:* ${formData.seats}\n*Date:* ${formData.date}\n*Time:* ${formData.time}\n*Special Request:* ${formData.specialRequest || "None"}`;
    const whatsappURL = `https://wa.me/447368325136?text=${encodeURIComponent(message)}`;
    setTimeout(() => { window.open(whatsappURL, "_blank"); setIsLoading(false); }, 800);
  };

  return (
    <div className="text-center page-container">
      <Helmet>
        <title>Book a Table | LasGidi Restaurant</title>
        <meta name="description" content="Reserve a table at LasGidi Restaurant in Manchester and enjoy authentic Nigerian cuisine with your friends and family." />
        <meta name="keywords" content="LasGidi Restaurant, Book Table, Nigerian Cuisine, Manchester Restaurant, Table Reservation" />
        <meta property="og:title" content="Book a Table | LasGidi Restaurant" />
        <meta property="og:description" content="Reserve a table at LasGidi Restaurant in Manchester and enjoy authentic Nigerian cuisine with your friends and family." />
        <meta property="og:type" content="website" />
        <link rel="preload" as="image" href={bookTableImage} fetchpriority="high" />
      </Helmet>

      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[9999]">
          <div className="border-4 border-white/30 border-t-white rounded-full w-10 h-10 animate-spin" />
        </div>
      )}

      {/* Hero banner */}
      <div className="relative w-full h-[200px] mb-10 flex items-center standard-padding overflow-hidden">
        <img 
          src={bookTableImage} 
          alt="Book a Table" 
          className="absolute inset-0 w-full h-full object-cover"
          fetchpriority="high"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/60" />
        <h1 className="relative z-10 text-white text-2xl sm:text-4xl font-bold">Book a Table</h1>
      </div>

      <form
        className="mx-auto my-[50px] w-full max-w-[800px] p-6 sm:p-10 rounded-[30px] shadow-[rgba(255,255,255,0.15)_0px_12px_36px_0px] relative overflow-hidden mb-20"
        onSubmit={handleSubmit}
      >
        <img 
          src={reserveTableImage} 
          alt="" 
          className="absolute inset-0 w-full h-full object-cover opacity-20 -z-10" 
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/80 -z-10" />
        <h2 className="mb-5 text-white text-xl">Reserve your table now for an unforgettable dining experience!</h2>
        <div className="flex justify-center gap-5 flex-wrap">
          {[
            { type: "number", id: "seats", name: "seats", placeholder: "Number of seats", min: "1" },
            { type: "tel", id: "phone", name: "phone", placeholder: "Phone number" },
            { type: "email", id: "email", name: "email", placeholder: "Email address" },
            { type: "text", id: "firstName", name: "firstName", placeholder: "First name" },
            { type: "text", id: "lastName", name: "lastName", placeholder: "Last name" },
          ].map(({ type, id, name, placeholder, min }) => (
            <div key={id} className="mb-[15px] text-left">
              <input type={type} id={id} name={name} value={formData[name]} onChange={handleChange} placeholder={placeholder} required min={min} className={inputCls} />
            </div>
          ))}

          {/* Date */}
          <div className="mb-[15px] text-left">
            <input type={dateType} id="date" name="date" value={formData.date} min={new Date().toISOString().split("T")[0]} onChange={handleChange} placeholder="Select Date"
              onFocus={handleDateFocus} onBlur={handleDateBlur} required className={inputCls} />
          </div>

          {/* Time */}
          <div className="mb-[15px] text-left">
            <select id="time" name="time" value={formData.time} onChange={handleChange} required className={inputCls}>
              <option value="">Select time</option>
              {generateTimeOptions().map((t, i) => <option key={i} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Special request */}
          <div className="mb-[15px] text-left">
            <textarea id="specialRequest" name="specialRequest" value={formData.specialRequest} onChange={handleChange} placeholder="Special request" rows="4" className={`${inputCls} resize-none`} />
          </div>
        </div>

        <button type="submit" disabled={isLoading}
          className="mt-5 px-5 py-2.5 bg-brand-orange text-black font-bold border-none text-base cursor-pointer rounded transition-opacity duration-300 hover:opacity-70 disabled:opacity-50 font-[inherit]">
          {isLoading ? "Booking..." : "Book Now"}
        </button>
      </form>
    </div>
  );
}

export default BookTablePage;
